import { fetch_requestor_application_audit_logs } from '@/services/requestor_portal_service';
import { fetch_submitted_risk_assessment_answers } from '@/services/risk_service';
import { getXVerdictFullConfigByEntityType } from '@/services/xverdict_entity_config_service';
import { mapRiskBindingsToAssessments } from '@/services/xverdict_config_mapper';
import { fetch_entity_profiles } from '../CompanyCases/company_cases_response_helpers';
import { load_visible_open_company_cases } from '../CompanyCases/company_cases_loading';
import { to_number } from '../CompanyCases/company_cases_common';
import { load_billable_report_rows } from './company_reports_billable_helpers';
import { risk_report_row } from './company_reports_download_helpers';

export function load_report_sources(params = {}, case_options = {}) {
  return load_case_reports(case_options).then((cases) =>
    Promise.all([
      Promise.resolve(cases),
      load_audit_logs(cases, params),
      load_profile_reports(params, cases),
    ])
  );
}

export function load_report_enrichments(cases = [], profiles = [], params = {}) {
  return Promise.all([
    load_submitted_risk_records(cases, params),
    load_billable_report_rows(profiles),
  ]);
}

export function report_load_error(error) {
  return error instanceof Error ? error : new Error('Failed to load company reports.');
}

async function load_case_reports({
  project_id = null,
  current_role = '',
  can_route_cases = false,
} = {}) {
  const cases = await load_visible_open_company_cases({
    project_id,
    current_role,
    can_route_cases,
  });

  return unique_case_list(cases);
}

async function load_audit_logs(cases = [], params = {}) {
  try {
    const logs = await fetch_requestor_application_audit_logs(params);
    return visible_audit_logs(logs, cases);
  } catch {
    return [];
  }
}

async function fetch_profiles(params = {}) {
  try {
    return await fetch_entity_profiles({ ...params, page: 1, page_size: 100 });
  } catch {
    return [];
  }
}

async function load_profile_reports(params = {}, cases = []) {
  const profiles = await fetch_profiles(params);
  return filter_profiles_to_visible_cases(profiles, cases);
}

async function load_submitted_risk_records(cases = [], params = {}) {
  const assessment_cache = new Map();
  const responses = await Promise.allSettled(
    cases.map((case_item) => submitted_risk_for_case(case_item, assessment_cache, params))
  );

  return responses
    .filter((response) => response.status === 'fulfilled' && response.value)
    .map((response) => response.value);
}

async function submitted_risk_for_case(case_item = {}, assessment_cache = new Map(), params = {}) {
  const entity_id = to_number(case_item.entity_profile_id);
  const assessments = await assessments_for_case(case_item, assessment_cache, params);
  if (!entity_id || !assessments.length) return null;

  const responses = await Promise.allSettled(
    assessments
      .map((assessment) => to_number(assessment.id))
      .filter(Boolean)
      .map((assessment_id) => fetch_submitted_risk_assessment_answers({ entity_id, assessment_id }))
  );
  const submitted = responses
    .filter((response) => response.status === 'fulfilled')
    .map((response) => response.value?.data || response.value)
    .find(has_risk_result);

  return submitted ? risk_report_row(case_item, submitted) : null;
}

async function assessments_for_case(case_item = {}, cache = new Map(), params = {}) {
  const entity_type_id = to_number(case_item.entity_type_id);
  if (!entity_type_id) return [];
  if (cache.has(entity_type_id)) return cache.get(entity_type_id);

  try {
    const config_data = await getXVerdictFullConfigByEntityType({
      entity_type_id,
      ...params,
    });
    const assessments = mapRiskBindingsToAssessments(config_data);
    cache.set(entity_type_id, assessments);
    return assessments;
  } catch {
    cache.set(entity_type_id, []);
    return [];
  }
}

function filter_profiles_to_visible_cases(profiles = [], cases = []) {
  const visible_entity_profile_ids = new Set(
    cases.map((case_item) => to_number(case_item?.entity_profile_id)).filter(Boolean)
  );
  const visible_application_ids = new Set(
    cases.map((case_item) => to_number(case_item?.application_id)).filter(Boolean)
  );

  if (!visible_entity_profile_ids.size && !visible_application_ids.size) {
    return [];
  }

  return profiles.filter((profile) => {
    const entity_profile_id = to_number(profile?.entity_profile_id || profile?.id);
    const application_id = to_number(
      profile?.application_id || profile?.latest_application_id || profile?.current_application_id
    );

    return visible_entity_profile_ids.has(entity_profile_id)
      || visible_application_ids.has(application_id);
  });
}

function visible_audit_logs(logs = [], cases = []) {
  const visible_application_ids = new Set(
    cases.map((case_item) => String(to_number(case_item?.application_id) || '')).filter(Boolean)
  );
  const visible_case_codes = new Set(
    cases.map((case_item) => normalized_reference(case_item?.case_code)).filter(Boolean)
  );

  if (!visible_application_ids.size && !visible_case_codes.size) {
    return [];
  }

  return logs.filter((log) => {
    const application_ref = String(to_number(log?.application_id) || '');
    if (application_ref && visible_application_ids.has(application_ref)) {
      return true;
    }

    const references = [
      log?.case_code,
      log?.application_code,
      log?.reference,
      log?.message,
      log?.action,
      log?.action_type,
    ].map(normalized_reference).filter(Boolean);

    return references.some((reference) =>
      Array.from(visible_case_codes).some((case_code) =>
        reference.includes(case_code) || case_code.includes(reference)
      )
    );
  });
}

function unique_case_list(cases = []) {
  const seen = new Set();

  return cases.filter((case_item) => {
    const key = unique_case_key(case_item);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function unique_case_key(case_item = {}) {
  return [
    to_number(case_item?.application_id),
    to_number(case_item?.entity_profile_id),
    normalized_reference(case_item?.case_code),
  ].find(Boolean) || null;
}

function has_risk_result(result = {}) {
  return [result.total_score, result.risk_score, result.score, result.risk_level, result.risk_band]
    .some((value) => value !== undefined && value !== null && value !== '');
}

function normalized_reference(value = '') {
  return String(value || '').trim().toLowerCase();
}
